/**
 * TextEditOverlay — in-place DOM editor for text annotations.
 *
 * Rendered inside the annotation container, absolutely positioned above the
 * canvas and pixel-matched to TextAnnotation's canvas rendering (font, 50%
 * black background box, 3-image-px padding, bottom-left anchor) so committing
 * causes no visible jump. While it is mounted the annotation's canvas copy is
 * suppressed via `isEditing`, making this the single visual source of truth.
 */

import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';

import type { AnnotationLayer } from './annotation-layer';
import type { TextAnnotationData } from './types';

/** Matches the background-box padding in TextAnnotation.render(), image px. */
const TEXT_PADDING = 3;

export interface TextEditOverlayHandle {
    /** Commit the current draft (used to force-commit before save/tool switch). */
    commit(): void;
}

interface TextEditOverlayProps {
    layer: AnnotationLayer;
    /** Live annotation data from React state (style updates apply while editing). */
    annotation: TextAnnotationData;
    /** New annotations start empty; existing ones open with text selected. */
    isNew: boolean;
    onCommit: (id: string, text: string) => void;
    onCancel: (id: string) => void;
    ref?: Ref<TextEditOverlayHandle>;
}

export function TextEditOverlay({ layer, annotation, isNew, onCommit, onCancel, ref }: TextEditOverlayProps) {
    const editorRef = useRef<HTMLSpanElement>(null);
    /** Guards commit/cancel to run at most once (blur fires again on unmount). */
    const closedRef = useRef(false);
    // Bumped by the ResizeObserver so position/scale recompute on layout changes.
    const [, setLayoutTick] = useState(0);

    const commit = () => {
        if (closedRef.current) return;
        closedRef.current = true;
        onCommit(annotation.id, editorRef.current?.textContent ?? '');
    };

    const cancel = () => {
        if (closedRef.current) return;
        closedRef.current = true;
        onCancel(annotation.id);
    };

    useImperativeHandle(ref, () => ({ commit }));

    // Seed content and focus once on mount; select-all when editing existing text.
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        el.textContent = annotation.text;
        el.focus();
        if (!isNew && annotation.text.length > 0) {
            const range = document.createRange();
            range.selectNodeContents(el);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    }, []);

    // Track container layout changes (window resize, responsive clamping).
    useEffect(() => {
        const observer = new ResizeObserver(() => setLayoutTick((t) => t + 1));
        observer.observe(layer.getCanvas());
        return () => observer.disconnect();
    }, [layer]);

    const scale = layer.getDisplayScale();
    if (scale === 0) return null;

    const { position, style } = annotation;

    return (
        <span
            ref={editorRef}
            contentEditable
            role="textbox"
            aria-label="Annotation text"
            data-annotation-text-editor=""
            spellCheck={false}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    commit();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    cancel();
                }
            }}
            onBlur={commit}
            onPaste={(e) => {
                // Plain text only, single line: strip formatting and newlines.
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain').replace(/[\r\n]+/g, ' ');
                const selection = window.getSelection();
                if (!selection || selection.rangeCount === 0) return;
                const range = selection.getRangeAt(0);
                range.deleteContents();
                range.insertNode(document.createTextNode(text));
                range.collapse(false);
            }}
            style={{
                position: 'absolute',
                left: (position.x - TEXT_PADDING) * scale,
                top: (position.y - style.fontSize - TEXT_PADDING) * scale,
                zIndex: 20,
                padding: TEXT_PADDING * scale,
                fontSize: style.fontSize * scale,
                lineHeight: `${style.fontSize * scale}px`,
                fontFamily: style.fontFamily,
                fontWeight: style.bold ? 'bold' : 'normal',
                fontStyle: style.italic ? 'italic' : 'normal',
                color: style.color,
                caretColor: style.color,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                outline: '1px dashed rgba(255, 255, 255, 0.9)',
                whiteSpace: 'pre',
                minWidth: '2ch',
                display: 'inline-block',
            }}
        />
    );
}
